/**
 * One declaration of what each entity type can DO (#1049).
 *
 * Before this, six capability registries lived in six places with exactly one
 * consistency check between any two of them, so "make products commentable" or
 * "let the assistant anchor to a store" meant editing files that had no idea
 * about each other — and forgetting one was silent. The web even kept a
 * hand-copied Set of the assistant-subject list, which is drift waiting to
 * happen.
 *
 * WHAT THIS IS: the authoritative capability declaration, plus per-surface
 * load-time checks that a declared capability actually has an adapter.
 *
 * WHAT THIS IS NOT: a one-file extension point. Per-entity behaviour still
 * needs its own adapter — an id-resolution target, an attachment descriptor
 * with its canServe/canDelete rules, a search collector that emits documents.
 * Declaring a capability here and skipping the adapter is precisely what the
 * startup checks in `apps/api/src/services/entityCapabilityChecks.ts` catch,
 * loudly, at boot rather than at the first request.
 *
 * BROWSER SAFETY: this module imports nothing but `zod`, so it is safe behind
 * the `@household/shared/entity-registry` subpath. Web code must never reach
 * for the root barrel (it drags csv-parse/Buffer into the bundle).
 */
import { z } from "zod";
/**
 * The master table. One row per entity-type NAME that any capability surface
 * knows about; the id-resolution catalog (`db/uuidMigration.ts` +
 * `services/entityIds.ts`) stays where it is and is CHECKED against this table
 * at boot rather than copied into it — it enumerates ~120 tables, most of which
 * carry no capability at all, and duplicating it here would trade one drift
 * risk for a longer one.
 */
export const entityCapabilityDeclarations = [
    {
        entityType: "asset",
        capabilities: ["attachable", "agentAttachTarget", "commentable", "searchIndexed", "assistantSubject", "qrTarget"]
    },
    {
        entityType: "location",
        capabilities: ["attachable", "agentAttachTarget", "commentable", "searchIndexed", "assistantSubject"],
        // #1050: places ARE assets since #147, so their polymorphic rows are
        // spelled `asset` everywhere. Search is deliberately NOT aliased — places
        // index as their own `location` documents with their own route.
        aliasOf: { attachments: "asset", comments: "asset", audit: "asset", qr: "asset" },
        note: "A place is an asset with a place_type; only its search identity is separate."
    },
    {
        entityType: "product",
        capabilities: ["attachable", "agentAttachTarget", "commentable", "searchIndexed", "assistantSubject"],
        // #1070/#1045: `agentAttachTarget` was deferred out of #1069 so the epic's
        // implementation PRs never contended for the tool-catalog mirror window.
        // A product meets the same safe-set criteria as every other target — it is
        // household-visible and household-scoped — and its descriptor is already
        // `canDelete: true`, so a file the assistant attaches can be removed again.
        note: "Assistant-attachable since #1070; the descriptor's canDelete is what makes that reversible."
    },
    {
        entityType: "asset_service_record",
        capabilities: ["attachable", "commentable", "searchIndexed"],
        // #1204. Attachable is the point of the entity — the invoice and the photo
        // of the replaced part are what make a service record worth keeping — and
        // commentable because "he said the next one needs doing in spring" is the
        // sort of thing that belongs beside the record, not in its notes field.
        //
        // Deliberately NOT an agentAttachTarget and NOT an assistantSubject: the
        // whole of #1180 is built with the assistant surface frozen (2026-08-16
        // owner decision), and both of those capabilities are agent-surface. They
        // are the natural follow-up when that freeze lifts.
        note: "#1204 maintenance stream. Agent capabilities deliberately omitted while the #1180 assistant freeze holds."
    },
    {
        entityType: "asset_acquisition",
        capabilities: ["attachable", "commentable", "searchIndexed"],
        // #1185. Attachable because researching a purchase produces paperwork — a
        // written quote, a spec sheet, a photo of the model number on the old one —
        // and it belongs to the DECISION rather than to a thing the household does
        // not own yet. Commentable because choosing between three dishwashers is a
        // conversation ("the quiet one is worth the extra fifty"), which is the
        // same argument #1204 and #1184 made for their records. Indexed so "what
        // was that fridge we were looking at?" reaches the wishlist rather than
        // finding nothing.
        //
        // Deliberately NOT an agentAttachTarget and NOT an assistantSubject, like
        // every entity this epic has registered: both are agent-surface, which the
        // #1180 assistant freeze (2026-08-16 owner decision) puts out of scope.
        note: "#1185 acquisitions. Agent capabilities deliberately omitted while the #1180 assistant freeze holds."
    },
    {
        entityType: "asset_listing",
        capabilities: ["attachable", "commentable", "searchIndexed"],
        // #1184. Attachable because the listing photos are the listing — the four
        // pictures somebody took of the bike against a garage door belong to the
        // ad, not to the thing, and they stop being wanted when it sells.
        // Commentable because a sale is a conversation ("he's offered 120, collects
        // Saturday") that belongs beside the listing rather than inside its notes.
        // Indexed so "who did we sell the mower to?" reaches the record.
        //
        // Deliberately NOT an agentAttachTarget and NOT an assistantSubject, like
        // the service records and policies above: both are agent-surface, which the
        // #1180 assistant freeze (2026-08-16 owner decision) puts out of scope.
        note: "#1184 sale lifecycle. Agent capabilities deliberately omitted while the #1180 assistant freeze holds."
    },
    {
        entityType: "insurance_policy",
        capabilities: ["attachable", "searchIndexed"],
        // #1210. Attachable is half the point of the entity — the declarations
        // page and the renewal letter are what somebody needs at the moment they
        // are making a claim, and an acceptance criterion says so in as many words.
        // Indexed because "what covers the ring?" is a search, and the insurer and
        // policy number are exactly the strings people half-remember.
        //
        // Deliberately NOT commentable: a policy's own `notes` is the place for
        // "called them about the deductible", and a comment thread would be a
        // second one. Deliberately NOT an agentAttachTarget or assistantSubject —
        // both are agent-surface, which the #1180 assistant freeze (2026-08-16
        // owner decision) puts out of scope for this whole epic.
        note: "#1210 insurance. Agent capabilities deliberately omitted while the #1180 assistant freeze holds."
    },
    { entityType: "recipe", capabilities: ["attachable", "agentAttachTarget", "commentable", "searchIndexed", "assistantSubject"] },
    { entityType: "task", capabilities: ["attachable", "agentAttachTarget", "commentable", "searchIndexed", "assistantSubject"] },
    { entityType: "project", capabilities: ["attachable", "agentAttachTarget", "searchIndexed", "assistantSubject"] },
    { entityType: "shopping_list", capabilities: ["attachable", "agentAttachTarget", "searchIndexed"] },
    { entityType: "markdown_document", capabilities: ["attachable", "agentAttachTarget", "searchIndexed"] },
    {
        entityType: "device",
        capabilities: ["attachable", "agentAttachTarget", "searchPersistedOnly"],
        note: "#629: not indexed, but assistant facts about BMS devices validate against it."
    },
    { entityType: "tag", capabilities: ["searchIndexed"] },
    { entityType: "stock_lot", capabilities: ["searchIndexed"] },
    { entityType: "meal_template", capabilities: ["searchIndexed"] },
    { entityType: "shopping_item", capabilities: ["searchIndexed"] },
    { entityType: "shopping_trip", capabilities: ["attachable", "searchIndexed"] },
    { entityType: "store", capabilities: ["searchIndexed"] },
    { entityType: "chore_template", capabilities: ["searchIndexed"] },
    { entityType: "chore_category", capabilities: ["attachable", "searchIndexed"] },
    { entityType: "attachment", capabilities: ["searchIndexed"] },
    { entityType: "document", capabilities: ["searchIndexed"] },
    { entityType: "comment", capabilities: ["searchIndexed"] },
    { entityType: "shopping_trip_item", capabilities: ["attachable"] },
    { entityType: "document_blob", capabilities: ["attachable"] },
    { entityType: "feedback", capabilities: ["attachable"] },
    { entityType: "finance_transaction", capabilities: ["attachable"], note: "Finance-gated on every serving path by its descriptor." },
    { entityType: "recurring_item", capabilities: ["attachable"], note: "Finance-gated on every serving path by its descriptor." },
    { entityType: "wifi_network", capabilities: ["attachable"] },
    { entityType: "network_device", capabilities: ["attachable", "searchPersistedOnly"], note: "#629 read-coverage, as device." },
    { entityType: "floor_plan", capabilities: ["attachable", "searchPersistedOnly"], note: "#629 read-coverage, as device." },
    {
        entityType: "floor_room",
        capabilities: ["attachable", "searchPersistedOnly"],
        // #1016 read-coverage: `floorplan.summary` and `floorplan.room_lookup` cite
        // a room as the source entity of a fact, so an agent fact has to validate
        // against this name. Still never indexed — the same shape `floor_plan` and
        // `network_device` took for #629.
        note: "#1016 read-coverage; agent facts cite a room, the indexer never emits one."
    },
    { entityType: "recipe_source", capabilities: ["attachable"] },
    {
        entityType: "agent_activity",
        capabilities: ["searchPersistedOnly"],
        note: "An agent tool run cited as a fact source; never indexed."
    },
    // NOT deprecated any more (#1201). `vendors` was born with the renovations
    // lite ERP and left routeless by the projects refactor, and the line below
    // used to say it was waiting for #242's destructive migration. It is now the
    // household's service-provider directory, with its own routes, its own
    // screen, and a link from every asset service record — so it is `attachable`
    // as well (a vendor's quote, licence or certificate of insurance belongs to
    // the vendor, not to any one job) and stays indexed. `docs/projects-module-refactor-plan.md`
    // records the same reversal beside its drop list.
    {
        entityType: "vendor",
        capabilities: ["attachable", "searchIndexed"],
        note: "The household's service providers (#1201). Adopted from the projects lite ERP rather than re-created; no longer on #242's drop list."
    },
    // Deprecated project sub-entities. Persisted chunks indexed before the
    // projects refactor still have to validate, so these stay until #242's
    // destructive migration and the reindex that follows it.
    { entityType: "project_work_item", capabilities: ["attachable", "searchPersistedOnly"], note: "Deprecated (#242)." },
    { entityType: "project_quote", capabilities: ["attachable", "searchPersistedOnly"], note: "Deprecated (#242)." },
    { entityType: "project_contract", capabilities: ["attachable", "searchPersistedOnly"], note: "Deprecated (#242)." },
    { entityType: "project_permit", capabilities: ["attachable", "searchPersistedOnly"], note: "Deprecated (#242)." },
    { entityType: "project_bom_item", capabilities: ["searchPersistedOnly"], note: "Deprecated (#242)." },
    { entityType: "project_decision", capabilities: ["searchPersistedOnly"], note: "Deprecated (#242)." }
];
const declarationByType = new Map(entityCapabilityDeclarations.map((row) => [row.entityType, row]));
if (declarationByType.size !== entityCapabilityDeclarations.length) {
    throw new Error("Entity capability declarations contain a duplicate entityType");
}
export function entityCapabilityDeclaration(entityType) {
    return declarationByType.get(entityType);
}
export function hasEntityCapability(entityType, capability) {
    return declarationByType.get(entityType)?.capabilities.includes(capability) ?? false;
}
/**
 * The canonical spelling to store for a polymorphic row on a given surface.
 *
 * Callers pass whatever spelling the client used; what comes back is what goes
 * in the column. Unknown types pass through unchanged — validation is the
 * surface's job, not this function's.
 */
export function canonicalEntityTypeFor(surface, entityType) {
    return declarationByType.get(entityType)?.aliasOf?.[surface] ?? entityType;
}
function typesWith(capability) {
    return entityCapabilityDeclarations
        .filter((row) => row.capabilities.includes(capability))
        .map((row) => row.entityType);
}
/** Every type that may own rows in `attachments`. */
export const attachableEntityTypeNames = typesWith("attachable");
/** The safe subset the assistant's attach tool may target. */
export const agentAttachTargetEntityTypeNames = typesWith("agentAttachTarget");
/** Every type that may own rows in `entity_comments`. */
export const commentEntityTypeNames = typesWith("commentable");
/** A chat may be pinned to one of these. */
export const assistantSubjectEntityTypeNames = typesWith("assistantSubject");
/** May own a row in `qr_tokens`. */
export const qrTargetEntityTypeNames = typesWith("qrTarget");
/**
 * What the indexer actually emits (#1049 PR-R3).
 *
 * Split from the persisted enum on purpose: the indexer should not be able to
 * emit a type nobody meant to index, while the persisted enum has to stay WIDE
 * or old chunks and #629 facts stop validating. Narrowing the query filter to
 * this list would 400 requests that succeed-empty today, so the filter keeps
 * reading the wide one.
 */
export const searchIndexableEntityTypeNames = typesWith("searchIndexed");
/** Everything the persisted chunk/fact validators accept: indexed + kept. */
export const semanticSearchEntityTypeNames = [...typesWith("searchIndexed"), ...typesWith("searchPersistedOnly")];
// Zod enums need a non-empty tuple; the arrays above are derived at module load
// and provably non-empty (the lock tests pin every one of them by value).
function enumFrom(values) {
    const [first, ...rest] = values;
    if (first === undefined)
        throw new Error("Cannot build an entity-type enum from an empty capability list");
    return z.enum([first, ...rest]);
}
export const attachableEntityTypeNameSchema = enumFrom(attachableEntityTypeNames);
export const agentAttachTargetEntityTypeNameSchema = enumFrom(agentAttachTargetEntityTypeNames);
export const commentEntityTypeNameSchema = enumFrom(commentEntityTypeNames);
export const assistantSubjectEntityTypeNameSchema = enumFrom(assistantSubjectEntityTypeNames);
export const semanticSearchEntityTypeNameSchema = enumFrom(semanticSearchEntityTypeNames);
